import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Check, Close, Search } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import DesignSystem from '../../designSystem';
import { getCategoryIconUrl } from '../../services/api';
import { useT } from '../../i18n/useT';
import { CATEGORY_TILE_PX, CATEGORY_TILE_SLOT_PX, categoryTileImageSx } from './categoryTileMetrics';

export type PickerCategory = {
  id: number;
  name: string;
  image?: string;
  icon?: string;
};

type CategoryPickerModalProps = {
  open: boolean;
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  categories: PickerCategory[];
  initiallySelectedIds: number[];
  onClose: () => void;
  onSave: (selectedIds: number[]) => void;
};

function categoryImageUrl(cat: PickerCategory): string | null {
  return getCategoryIconUrl(cat.image || cat.icon || undefined);
}

/** iOS vcChangeCategories-style image grid picker. */
export default function CategoryPickerModal({
  open,
  loading = false,
  saving = false,
  error = null,
  categories,
  initiallySelectedIds,
  onClose,
  onSave,
}: CategoryPickerModalProps) {
  const t = useT();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(new Set(initiallySelectedIds));
  }, [open, initiallySelectedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, query]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: DesignSystem.colors.canvas,
          backgroundImage: 'none',
          maxHeight: fullScreen ? '100%' : { xs: '92vh', sm: '85vh' },
          m: fullScreen ? 0 : undefined,
          borderRadius: fullScreen ? 0 : DesignSystem.borderRadius.xl,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
          pb: 1,
          pt: fullScreen ? 2 : undefined,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: DesignSystem.colors.textPrimary }}>
            {t('homeManageCategoriesTitle')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mt: 0.25 }}>
            {t('homeManageCategoriesHint')}
          </Typography>
        </Box>
        <IconButton aria-label={t('cancel')} onClick={onClose} disabled={saving} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('homeManageCategoriesSearch')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: DesignSystem.colors.textTertiary, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            flexShrink: 0,
            '& .MuiOutlinedInput-root': {
              backgroundColor: DesignSystem.colors.surface,
              borderRadius: DesignSystem.borderRadius.pill,
            },
          }}
        />

        {error ? (
          <Typography color="error" variant="body2" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: DesignSystem.colors.textSecondary }}>
            {t('homeManageCategoriesEmpty')}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: `repeat(auto-fill, minmax(${CATEGORY_TILE_SLOT_PX}px, 1fr))`,
                sm: `repeat(auto-fill, minmax(${CATEGORY_TILE_SLOT_PX}px, 1fr))`,
              },
              gap: 1,
              justifyItems: 'center',
              overflowY: 'auto',
              pb: 1,
            }}
          >
            {filtered.map((cat) => {
              const isSelected = selected.has(cat.id);
              const img = categoryImageUrl(cat);
              return (
                <Box
                  key={cat.id}
                  component="button"
                  type="button"
                  onClick={() => toggle(cat.id)}
                  aria-pressed={isSelected}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    width: CATEGORY_TILE_SLOT_PX,
                    maxWidth: CATEGORY_TILE_SLOT_PX,
                    p: 0,
                    border: 0,
                    cursor: 'pointer',
                    background: 'transparent',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    opacity: isSelected ? 1 : 0.55,
                    transition: 'opacity 120ms ease',
                    '&:focus-visible .picker-avatar': {
                      boxShadow: `0 0 0 3px ${DesignSystem.colors.actionLight}`,
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', width: CATEGORY_TILE_PX, height: CATEGORY_TILE_PX }}>
                    <Box
                      className="picker-avatar"
                      sx={{
                        width: CATEGORY_TILE_PX,
                        height: CATEGORY_TILE_PX,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: DesignSystem.colors.surfaceElevated,
                        border: isSelected
                          ? `2px solid ${DesignSystem.colors.action}`
                          : `1.5px solid ${DesignSystem.colors.separator}`,
                        boxSizing: 'border-box',
                        transition: 'border-color 120ms ease',
                      }}
                    >
                      {img ? (
                        <Box
                          component="img"
                          src={img}
                          alt=""
                          draggable={false}
                          onDragStart={(e: React.DragEvent<HTMLImageElement>) => e.preventDefault()}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = 'none';
                          }}
                          sx={categoryTileImageSx}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'grid',
                            placeItems: 'center',
                            color: DesignSystem.colors.textTertiary,
                            fontSize: CATEGORY_TILE_PX * 0.36,
                            fontWeight: 700,
                          }}
                        >
                          {cat.name.charAt(0)}
                        </Box>
                      )}
                    </Box>
                    {isSelected ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          backgroundColor: DesignSystem.colors.action,
                          border: `1.5px solid ${DesignSystem.colors.surface}`,
                          display: 'grid',
                          placeItems: 'center',
                          color: DesignSystem.colors.onPrimary,
                        }}
                      >
                        <Check sx={{ fontSize: 12 }} />
                      </Box>
                    ) : null}
                  </Box>
                  <Typography
                    title={cat.name}
                    sx={{
                      width: '100%',
                      color: DesignSystem.colors.textPrimary,
                      fontSize: 11,
                      fontWeight: isSelected ? 700 : 600,
                      lineHeight: 1.15,
                      textAlign: 'center',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      minHeight: 24,
                    }}
                  >
                    {cat.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={loading || saving}
          onClick={() => onSave(Array.from(selected))}
          sx={{
            py: 1.35,
            fontWeight: 700,
            fontSize: 16,
            textTransform: 'none',
            borderRadius: DesignSystem.borderRadius.lg,
            backgroundColor: DesignSystem.colors.action,
            '&:hover': { backgroundColor: DesignSystem.colors.actionDark },
          }}
        >
          {saving ? t('saving') : t('okay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
